const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/modules/CIS4m/hooks.ts';
import * as React from 'react';
import { RefObject, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { makeSelectCIS4m } from './store';
import {
    actionRemoveFromCIS4mDataObject,
    actionSetDefaultInCIS4mDataObject,
    actionSetInCIS4mDataObject,
} from './actions';
import { isValue } from './utils/data';
import { useCIS4mContext } from './context';
import { ValidationFunction } from '../../components/Forms/utils/validation';
import { useMadeSelector } from '../../utils/store';
import { TextInput, useOptionalText } from '../../utils/intlUtils';
import textMap from '../../modules/CIS4m/components/translations';

export interface CIS4mImperativeHandle {
    cis4mComponent: true;
    isValid: () => boolean;
    checkSave: () => boolean;
    onSave: () => void;
}

export function useCIS4mData<T = any>(): T | undefined {
    const { formId } = useCIS4mContext();
    const cis4mData = useMadeSelector(() => makeSelectCIS4m(formId), [formId]);
    return cis4mData?.dataObject.toJS();
}

export const useValueFromCIS4mImm = (path: string) => {
    const { formId } = useCIS4mContext();
    const cis4mData = useMadeSelector(() => makeSelectCIS4m(formId), [formId]);
    return cis4mData?.dataObject.getIn(_.toPath(path));
};

// adds checkSave and onSave to ref.current
export const addCIS4mComponentBehaviour = (
    ref: RefObject<CIS4mImperativeHandle | null>,
    isValid: () => boolean,
    checkSave: () => boolean,
    onSave: () => void
) => {
    useImperativeHandle(ref, () => ({
        cis4mComponent: true as const, // property to be checked so checkSave and onSave can be safely called; this also means that other components can be safely used inside a mobile form
        isValid,
        checkSave,
        onSave,
    }));
};

export const useCIS4m = (ref: any, checkSave: () => boolean = () => true, onSave: () => void = () => {}) => {
    const formRef = useRef<any>(null);

    addCIS4mComponentBehaviour(
        ref,
        () => {
            return formRef.current ? formRef.current.isValid() : false;
        },
        () => {
            return checkSave() && (formRef.current ? formRef.current.checkSave() : true);
        },
        () => {
            onSave();
            formRef.current?.onSave();
        }
    );

    return formRef;
};

// Make sure that checkSave and onSave of form components used in context of the component that uses this hook
// Returns clones of the children passed
export const useConnectToChildCIS4mComponents = (
    ref: any, // ref of the current component
    children: any, // most likely props.children
    checkSaveCallback: undefined | ((allowSave: boolean) => void) = undefined,
    propsToAdd: any = {}
) => {
    let formComponentsRefsCurrents: any[] = [];

    const referencedChildren = useMemo(() => {
        formComponentsRefsCurrents = [];

        const cloneAndStoreRef = (child: any, index: number) => {
            // Child can be null
            if (child) {
                // Stores the ref of a child so later checkSave and onSave can be called on it
                const storeChildRefCurrent = (refCurrent: any) => {
                    // only store ref.current if the component uses CIS4m behaviour
                    if (refCurrent?.cis4mComponent) {
                        // Need to store by index because this ref function can be executed several times
                        // (basically when there is a change in the component)
                        formComponentsRefsCurrents[index] = refCurrent;
                    }
                    return refCurrent;
                };

                // Filter for components wrapped in React.forwardRef. By only cloning these components, we prevent the "Function components cannot be given refs" warning
                // for simple components that do not need to participate in the CIS4m tree messaging (checkSave/onSave).
                // Note: $$typeof is a React internal; this check will need updating for React 19 where forwardRef is deprecated and refs are passed as regular props.
                const isForwardRef =
                    typeof child.type === 'object' && child.type.$$typeof === Symbol.for('react.forward_ref');
                if (isForwardRef) {
                    return React.cloneElement(child, {
                        ref: storeChildRefCurrent,
                        ...propsToAdd,
                    });
                }
            }
            return child;
        };

        return React.Children.map(children, cloneAndStoreRef);
    }, [children, propsToAdd]);

    const isValid = () => {
        let valid = true;
        _.compact(formComponentsRefsCurrents).forEach((childRefCurrent: any) => {
            const check = childRefCurrent.isValid();
            valid &&= check;
        });
        return valid;
    };

    const checkSave = () => {
        let allowSave = true;
        _.compact(formComponentsRefsCurrents).forEach((childRefCurrent: any) => {
            const check = childRefCurrent.checkSave();
            allowSave &&= check;
        });
        checkSaveCallback?.(allowSave);
        return allowSave;
    };

    const onSave = () => {
        _.compact(formComponentsRefsCurrents).forEach((childRefCurrent: any) => {
            childRefCurrent.onSave();
        });
    };

    addCIS4mComponentBehaviour(ref, isValid, checkSave, onSave);

    return referencedChildren;
};

export const useEstablishDeepTreeMessaging = (
    ref: any,
    checkSaveCallback: undefined | ((allowSave: boolean) => void) = undefined
) => {
    const childrenRef = useRef<any>([]);

    const treeMessagingRefGenerator = (index: any) => (ref: any) => (childrenRef.current[index] = ref);
    const treeMessagingRefReset = () => (childrenRef.current = []);

    const isValid = () => {
        const childrenRefs = _.compact(childrenRef.current);
        let valid = true;
        _.forEach(childrenRefs, (childRef: any) => {
            if (childRef.cis4mComponent) {
                const check = childRef.isValid();
                valid &&= check;
            }
        });
        return valid;
    };

    const checkSave = () => {
        const childrenRefs = _.compact(childrenRef.current);
        let allowSave = true;
        _.forEach(childrenRefs, (childRef: any) => {
            if (childRef.cis4mComponent) {
                const check = childRef.checkSave();
                allowSave &&= check;
            }
        });
        checkSaveCallback?.(allowSave);
        return allowSave;
    };

    const onSave = () => {
        const imperativeHandles = _.compact(childrenRef.current);
        _.forEach(imperativeHandles, (imperativeHandle: any) => {
            if (imperativeHandle.cis4mComponent) {
                imperativeHandle.onSave();
            }
        });
    };

    addCIS4mComponentBehaviour(ref, isValid, checkSave, onSave);

    return { treeMessagingRefGenerator, treeMessagingRefReset };
};

type UseValueMessagingProps = {
    checkSave?: () => boolean;
    hidden: boolean;
    initialValue: any;
    isValid?: () => boolean;
    onSave?: () => void;
    path: string;
};

export const useValueMessaging = (ref: any, props: UseValueMessagingProps) => {
    const {
        checkSave = () => true,
        hidden = false,
        initialValue,
        isValid = () => true,
        onSave = () => {},
        path,
    } = props;

    const { formId } = useCIS4mContext();

    const removeHiddenFieldValue = () => {
        if (hidden) {
            dispatch(actionRemoveFromCIS4mDataObject(formId, path));
        }
    };

    const onFieldSave = () => {
        removeHiddenFieldValue();
        onSave();
    };

    addCIS4mComponentBehaviour(ref, isValid, checkSave, onFieldSave);

    const dispatch = useDispatch<any>();

    const value = useValueFromCIS4mImm(path);

    useEffect(() => {
        if (_.isUndefined(value) && !_.isUndefined(initialValue) && !hidden) {
            dispatch(actionSetDefaultInCIS4mDataObject(formId, path, initialValue));
        }
    }, [value, initialValue, hidden]);

    return [value, (value: any) => dispatch(actionSetInCIS4mDataObject(formId, path, value))];
};

type UseCIS4mValidationProps = {
    checkValidity?: ValidationFunction;
    disabled: boolean;
    errorMessageWhenRequired?: TextInput;
    hidden: boolean;
    initialValue: any;
    path: string;
    required: boolean;
};

export const useCIS4mValidation = (ref: any, props: UseCIS4mValidationProps) => {
    const {
        checkValidity = () => undefined,
        disabled = false,
        errorMessageWhenRequired = textMap.mandatoryLabel,
        hidden = false,
        initialValue,
        path,
        required = false,
    } = props;

    const [errorMessage, setErrorMessage] = useState<TextInput>(undefined);

    const isValid = () => {
        if (hidden || disabled) return true;

        const hasValue = isValue(value);
        if (required && !hasValue) {
            return false;
        }

        const validationMessage = hasValue ? checkValidity(value) : undefined;
        return !validationMessage;
    };

    const checkSave = () => {
        if (!hidden && !disabled) {
            const hasValue = isValue(value);
            if (required && !hasValue) {
                setErrorMessage(errorMessageWhenRequired);
                return false;
            }

            const validationMessage = hasValue ? checkValidity(value) : undefined;
            if (validationMessage) {
                setErrorMessage(validationMessage);
                return false;
            }
        }
        setErrorMessage(undefined);
        return true;
    };

    const [value, setValue] = useValueMessaging(ref, {
        hidden,
        initialValue,
        isValid,
        checkSave,
        path,
    });

    // Remove error whenever a value is specified, the field is no longer required, or the field is hidden
    useEffect(() => {
        if ((isValue(value) && !checkValidity(value)) || !required || hidden) {
            setErrorMessage(undefined);
        }
    }, [value, required, hidden]);

    const returnedErrorMessage = useOptionalText(errorMessage);

    return [value, setValue, returnedErrorMessage];
};
